/**
 * HUSL Ad Centre — Shopify banners with app-parity carousel.
 * - Serves all active_ads for a position
 * - Autoplay 5s + loop (like mobile AdCarousel)
 * - Impression on slide change only while visible
 * - platform=shopify; anonymous session_id
 */
(function () {
  if (window.HuslAdCentre && window.HuslAdCentre.version === 3) return;

  var PLATFORM = 'shopify';
  var SESSION_KEY = 'husl_ad_session_id';
  var API_PATH = '/api/v2/advertisements';
  var AUTOPLAY_MS = 5000;
  var memorySessionId = null;

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function sessionId() {
    try {
      var existing = localStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      var created = uuid();
      localStorage.setItem(SESSION_KEY, created);
      return created;
    } catch (e) {
      if (!memorySessionId) memorySessionId = uuid();
      return memorySessionId;
    }
  }

  function resolveApiBase() {
    if (window.HuslEnv && typeof window.HuslEnv.apiBase === 'function') {
      return window.HuslEnv.apiBase();
    }
    var host = ((window.location && window.location.hostname) || '').toLowerCase();
    var shop = String(window.__HUSL_SHOP_DOMAIN__ || '').toLowerCase();
    function isStagingHost(h) {
      return h === 'localhost' || h === '127.0.0.1' || h.indexOf('staging.') === 0 || h === 'y0pv0t-qs.myshopify.com';
    }
    if (isStagingHost(host) || isStagingHost(shop)) return 'https://api-staging.huslbeauty.com';
    return 'https://api.huslbeauty.com';
  }

  function pickImage(ad) {
    if (!ad) return null;
    var width = window.innerWidth || 0;
    if (width >= 990 && ad.image_tablet_landscape) return ad.image_tablet_landscape;
    if (width >= 750 && ad.image_tablet_portrait) return ad.image_tablet_portrait;
    return ad.image || ad.image_tablet_portrait || ad.image_tablet_landscape || null;
  }

  function looksLikeUrl(value) {
    return /^https?:\/\//i.test(value) || value.charAt(0) === '/';
  }

  function isUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value
    );
  }

  /**
   * Resolve storefront href. Prefer custom_deeplink with a Shopify URL.
   * App-only destinations (wholesale_cart, UUID product/category ids) cannot
   * be mapped on the storefront — fall back unless a URL/handle is provided.
   */
  function destinationHref(ad, fallbackHref) {
    if (!ad) return fallbackHref || '#';
    var value = (ad.destination_value || '').trim();
    var type = ad.destination_type || '';

    if (looksLikeUrl(value)) return value;

    if (type === 'custom_deeplink' && value) return value;
    if (type === 'brand_page' && value) return '/collections/' + encodeURIComponent(value);
    if (type === 'product_detail' && value && !isUuid(value)) {
      return '/products/' + encodeURIComponent(value);
    }
    if (type === 'category_page' && value && !isUuid(value)) {
      return '/collections/' + encodeURIComponent(value);
    }

    return fallbackHref || '#';
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
    }).then(function (res) {
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function trackImpression(apiBase, positionSlug, creativeId) {
    return postJson(apiBase + API_PATH + '/impressions/', {
      creative_id: creativeId,
      position_slug: positionSlug,
      session_id: sessionId(),
      platform: PLATFORM,
    });
  }

  function trackClick(apiBase, positionSlug, creativeId) {
    return postJson(apiBase + API_PATH + '/clicks/', {
      creative_id: creativeId,
      position_slug: positionSlug,
      session_id: sessionId(),
      platform: PLATFORM,
    });
  }

  function hideOrShowFallback(root) {
    var showWhenEmpty = root.getAttribute('data-show-when-empty') === 'true';
    root.hidden = !showWhenEmpty;
    var fallback = root.querySelector('.husl-ad-centre__fallback');
    var carousel = root.querySelector('.husl-ad-centre__carousel');
    if (fallback) fallback.hidden = !showWhenEmpty;
    if (carousel) carousel.hidden = true;
  }

  function buildCarousel(root, ads, apiBase, positionSlug) {
    var wrap = root.querySelector('.husl-ad-centre__wrap');
    if (!wrap) return;

    var fallbackHref = root.getAttribute('data-fallback-href') || '#';
    var fallback = root.querySelector('.husl-ad-centre__fallback');
    if (fallback) fallback.hidden = true;

    var carousel = root.querySelector('.husl-ad-centre__carousel');
    if (!carousel) {
      carousel = document.createElement('div');
      carousel.className = 'husl-ad-centre__carousel';
      wrap.appendChild(carousel);
    }
    carousel.innerHTML = '';
    carousel.hidden = false;

    var viewport = document.createElement('div');
    viewport.className = 'husl-ad-centre__viewport';
    var track = document.createElement('div');
    track.className = 'husl-ad-centre__track';

    var state = {
      index: 0,
      timer: null,
      ads: ads,
      visible: false,
      swiped: false,
    };

    ads.forEach(function (ad, index) {
      var slide = document.createElement('a');
      slide.className = 'husl-ad-centre__slide';
      slide.href = destinationHref(ad, fallbackHref);
      slide.setAttribute('data-creative-id', ad.id || '');
      slide.setAttribute('data-index', String(index));
      slide.setAttribute('aria-label', 'Shop Now: ' + (ad.title || 'Promotional banner'));

      var imageUrl = pickImage(ad);
      if (imageUrl) {
        var img = document.createElement('img');
        img.className = 'husl-ad-centre__img';
        img.src = imageUrl;
        img.alt = ad.title || 'Promotional banner';
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.decoding = 'async';
        img.draggable = false;
        slide.appendChild(img);
      } else {
        var empty = document.createElement('div');
        empty.className = 'husl-ad-centre__placeholder';
        empty.textContent = ad.title || 'Ad';
        slide.appendChild(empty);
      }
            var cta = document.createElement('span');
      cta.className = 'husl-ad-centre__cta';
      cta.textContent = 'Shop Now';
      slide.appendChild(cta);

      slide.addEventListener('click', function (event) {
        if (state.swiped) {
          event.preventDefault();
          state.swiped = false;
          return;
        }
        if (!ad || !ad.id) return;
        event.preventDefault();
        var target = destinationHref(ad, fallbackHref);
        trackClick(apiBase, positionSlug, ad.id)
          .then(function (payload) {
            var data = (payload && payload.data) || {};
            var resolved = destinationHref(
              {
                destination_type: data.destination_type || ad.destination_type,
                destination_value: data.destination_value || ad.destination_value,
              },
              fallbackHref
            );
            if (resolved && resolved !== '#') target = resolved;
          })
          .catch(function () {})
          .then(function () {
            if (target && target !== '#') window.location.href = target;
          });
      });

      track.appendChild(slide);
    });

    viewport.appendChild(track);
    carousel.appendChild(viewport);

    function setIndex(next, recordImpression) {
      if (!ads.length) return;
      var count = ads.length;
      state.index = ((next % count) + count) % count;
      track.style.transform = 'translateX(' + -state.index * 100 + '%)';
      if (dots) {
        var buttons = dots.querySelectorAll('button');
        for (var i = 0; i < buttons.length; i++) {
          buttons[i].setAttribute('aria-current', i === state.index ? 'true' : 'false');
        }
      }
      if (recordImpression !== false && state.visible) {
        var ad = ads[state.index];
        if (ad && ad.id) {
          trackImpression(apiBase, positionSlug, ad.id).catch(function () {});
        }
      }
    }

    var dots = null;
    if (ads.length > 1) {
      dots = document.createElement('div');
      dots.className = 'husl-ad-centre__dots';
      dots.setAttribute('role', 'tablist');
      ads.forEach(function (_ad, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'husl-ad-centre__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () {
          stopAutoplay();
          setIndex(i, true);
          if (state.visible) startAutoplay();
        });
        dots.appendChild(dot);
      });
      carousel.appendChild(dots);
    }

    function startAutoplay() {
      stopAutoplay();
      if (ads.length < 2 || !state.visible) return;
      state.timer = window.setInterval(function () {
        setIndex(state.index + 1, true);
      }, AUTOPLAY_MS);
    }

    function stopAutoplay() {
      if (state.timer) {
        window.clearInterval(state.timer);
        state.timer = null;
      }
    }

    var touchStartX = 0;
    var touchDeltaX = 0;
    viewport.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || !e.touches.length) return;
        touchStartX = e.touches[0].clientX;
        touchDeltaX = 0;
        state.swiped = false;
        stopAutoplay();
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchmove',
      function (e) {
        if (!e.touches || !e.touches.length) return;
        touchDeltaX = e.touches[0].clientX - touchStartX;
        if (Math.abs(touchDeltaX) > 10) state.swiped = true;
      },
      { passive: true }
    );
    viewport.addEventListener('touchend', function () {
      if (Math.abs(touchDeltaX) > 40) {
        state.swiped = true;
        setIndex(state.index + (touchDeltaX < 0 ? 1 : -1), true);
      }
      if (state.visible) startAutoplay();
      touchDeltaX = 0;
      window.setTimeout(function () {
        state.swiped = false;
      }, 300);
    });

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', function () {
      if (state.visible) startAutoplay();
    });

    root.hidden = false;

    if (!('IntersectionObserver' in window)) {
      state.visible = true;
      setIndex(0, true);
      startAutoplay();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var wasHidden = !state.visible;
            state.visible = true;
            if (wasHidden) {
              setIndex(state.index, true);
              startAutoplay();
            }
          } else {
            state.visible = false;
            stopAutoplay();
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(root);
  }

  function initRoot(root) {
    if (root._huslAdInit) return;
    root._huslAdInit = true;

    var positionSlug = root.getAttribute('data-position-slug');
    if (!positionSlug) return;

    var apiBase = resolveApiBase();

    if (root.getAttribute('data-show-when-empty') !== 'true') {
      root.hidden = true;
    }

    fetch(apiBase + API_PATH + '/positions/' + encodeURIComponent(positionSlug) + '/active/', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('ad fetch failed');
        return res.json();
      })
      .then(function (payload) {
        var data = (payload && payload.data) || {};
        var ads = data.active_ads || [];
        if (!ads.length) {
          hideOrShowFallback(root);
          return;
        }
        buildCarousel(root, ads, apiBase, positionSlug);
      })
      .catch(function () {
        hideOrShowFallback(root);
      });
  }

  function initAll() {
    var nodes = document.querySelectorAll('.husl-ad-centre');
    for (var i = 0; i < nodes.length; i++) initRoot(nodes[i]);
  }

  window.HuslAdCentre = { init: initAll, initRoot: initRoot, version: 3 };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
